import { toast } from 'sonner'
import { paths } from '@/routes/paths'
import { useState, useEffect } from 'react'
import { fError } from '@/utils/format-error'
import { useBoolean } from '@/hooks/use-boolean'
import { useRouter } from '@/routes/hooks/use-router'
import axiosInstance, { endpoints } from '@/lib/axios'
import CustomPagination from '@/components/pagination/custom-pagination'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LoadingSection from '@/components/loading-indicator/loading-section'

import type {
  Article,
  ArticleFilters,
  PaginationMeta,
  Pagination,
} from '../types'

import ArticleTable from '../article-table'
import ArticleFilter from '../article-filter'

const PAGE_SIZE = 10

export default function ArticleView() {
  const router = useRouter()
  const loading = useBoolean(true)
  const [articles, setArticles] = useState<Article[]>([])
  const [pageMeta, setPageMeta] = useState<PaginationMeta>({
    limit: PAGE_SIZE,
    page: 1,
    total: 0,
  })
  const [filters, setFilters] = useState<ArticleFilters>({
    page: 1,
    limit: PAGE_SIZE,
    search: '',
    status: '',
    authorId: '',
    orderBy: 'createdAt:desc',
  })

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    article: Article | null
  }>({
    open: false,
    article: null,
  })

  // Loading state for delete operation
  const [deleteLoading, setDeleteLoading] = useState(false)

  const fetchArticles = async () => {
    try {
      loading.onTrue()

      // Transform filters for API
      const apiFilters = {
        ...filters,
        page: filters.page,
        limit: filters.limit,
        isPublished:
          filters.status === 'published'
            ? true
            : filters.status === 'draft'
              ? false
              : undefined,
      }

      // Remove status from API params since we're using isPublished
      const { data } = await axiosInstance.get<Pagination<Article>>(
        endpoints.articles.list,
        {
          params: apiFilters,
        },
      )

      setArticles(data.items)
      setPageMeta({
        limit: data.limit,
        page: data.page,
        total: data.total,
      })
    } catch (error) {
      toast.error(fError(error))
    } finally {
      loading.onFalse()
    }
  }

  useEffect(() => {
    fetchArticles()
  }, [filters])

  const handleFiltersChange = (newFilters: ArticleFilters) => {
    setFilters(newFilters)
  }

  const handleAddClick = () => {
    router.push(paths.articleCreate)
  }

  const handleEdit = (article: Article) => {
    router.push(paths.articleEdit(article.id))
  }

  const handleDelete = (article: Article) => {
    setConfirmDialog({
      open: true,
      article,
    })
  }

  const handleConfirmDelete = async () => {
    if (!confirmDialog.article || deleteLoading) return

    try {
      setDeleteLoading(true)

      await axiosInstance.delete(
        endpoints.articles.delete(confirmDialog.article.id),
      )
      toast.success('ลบ RFC Talk สำเร็จ')

      // Refresh the list
      fetchArticles()
    } catch (error) {
      toast.error(fError(error))
    } finally {
      setDeleteLoading(false)
    }
  }

  const handlePageChange = (page: number) => {
    setFilters({
      ...filters,
      page,
    })
  }

  return (
    <div className="w-full">
      <LoadingSection loading={loading.value}>
        <div className="space-y-6">
          {/* Filter Section */}
          <ArticleFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onAddClick={handleAddClick}
          />

          {/* Table Section */}
          <ArticleTable
            articles={articles}
            loading={loading.value}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />

          {/* Pagination */}
          {Math.ceil(pageMeta.total / pageMeta.limit) > 1 && (
            <CustomPagination
              currentPage={filters.page}
              totalPages={Math.ceil(pageMeta.total / pageMeta.limit)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </LoadingSection>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ open, article: null })}
        title="ยืนยันการลบ"
        description={`คุณต้องการลบ RFC Talk "${confirmDialog.article?.title}" หรือไม่? การดำเนินการนี้ไม่สามารถยกเลิกได้`}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  )
}
